import type { HelpGuide, HelpPage } from '@/help/helpPages';
import MiniSearch from 'minisearch';
import { ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHelpPages } from '@/composables/useHelpPages';
import { translateHelpTitle } from '@/help/helpPages';

export interface HelpSearchResult {
  path: string;
  title: string;
  guide: string;
}

interface HelpSearchDocument extends HelpSearchResult {
  body: string;
}

const MAX_RESULTS = 10;

const guideTitles: Record<HelpGuide, { key: string; count?: number }> = {
  publisher: { key: 'smpw.publisher', count: 2 },
  admin: { key: 'global.administration' },
  maintenance: { key: 'maintenance.title' },
  glossary: { key: 'glossary.title' },
};

/**
 * Every quoted, dotted string in a page's source that could be a translation key: the keys passed
 * to `t()`, `<i18n-t keypath>` and to child components. Callers keep the ones that really are
 * messages.
 */
export function extractTranslationKeys(source: string): string[] {
  const keys = new Set<string>();
  for (const match of source.matchAll(/['"`]([a-z][\w-]*(?:\.\w+)+)['"`]/g)) {
    keys.add(match[1]);
  }
  return [...keys];
}

/**
 * The text written straight into a component's template rather than translated, such as code
 * samples, setting names and product names in slots. Tags, attributes, comments and `{{ }}`
 * expressions are left out.
 */
export function extractTemplateText(source: string): string {
  const start = source.indexOf('<template');
  const end = source.lastIndexOf('</template>');
  if (start === -1 || end <= start) {
    return '';
  }
  return source
    .slice(start, end)
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/\{\{[\s\S]*?\}\}/g, ' ')
    .replace(/<\/?[a-z][^>"']*(?:(?:"[^"]*"|'[^']*')[^>"']*)*>/gi, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A page's source followed by the sources of the shared help components it renders
 * (`@/components/help/...`), and theirs in turn, so their text counts as the page's.
 */
export function collectSources(source: string, componentSources: Record<string, string>, seen = new Set<string>()): string[] {
  const nested = [
    ...source.matchAll(/from\s+['"](@\/components\/help\/[^'"]+\.vue)['"]/g),
  ]
    .map(match => match[1])
    .filter(specifier => componentSources[specifier] !== undefined && !seen.has(specifier));
  nested.forEach(specifier => seen.add(specifier));

  return [
    source,
    ...nested.flatMap(specifier => collectSources(componentSources[specifier], componentSources, seen)),
  ];
}

/** Removes `{placeholder}` tokens, whose values the page fills in with markup or settings. */
export function stripPlaceholders(text: string): string {
  return text.replace(/\{[^}]*\}/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Full-text search over the User Guide pages the current user may see, in the current language.
 *
 * The index is built from each page's own source and the shared help components it renders: every
 * translation key they use is looked up in the current locale (falling back to English like the
 * page does), and the text written straight into their templates is added as it is. Nothing is
 * fetched or built until `prepare()` is called, and the index is rebuilt when the language changes.
 */
export function useHelpSearch() {
  const { t, te, locale } = useI18n();
  const { visibleHelpPages, resolveHelpTitle } = useHelpPages();

  const index = shallowRef<MiniSearch<HelpSearchDocument> | null>(null);
  const isPreparing = ref(false);
  let preparing: Promise<void> | null = null;

  function pageDocument(page: HelpPage, sources: string[]): HelpSearchDocument {
    const keys = new Set(sources.flatMap(extractTranslationKeys));
    const translated = [...keys]
      .filter(key => te(key, 'en'))
      .map(key => stripPlaceholders(t(key)));
    const body = [
      ...translated,
      ...sources.map(extractTemplateText),
    ].join(' ');

    return {
      path: page.path,
      title: translateHelpTitle(resolveHelpTitle(page), t),
      guide: translateHelpTitle(guideTitles[page.guide], t),
      body,
    };
  }

  async function build(): Promise<void> {
    const { helpComponentSources, helpPageSources } = await import('@/help/helpPageSources');
    const search = new MiniSearch<HelpSearchDocument>({
      idField: 'path',
      fields: ['title', 'guide', 'body'],
      storeFields: ['path', 'title', 'guide'],
      searchOptions: {
        boost: { title: 3 },
        prefix: true,
        fuzzy: 0.2,
        combineWith: 'AND',
      },
    });
    // Translated only after the sources have loaded, so a language change while loading is picked up.
    search.addAll(visibleHelpPages().map(page => pageDocument(page, collectSources(helpPageSources[page.file] ?? '', helpComponentSources))));
    index.value = search;
  }

  /** Builds the index if it is not built yet. Safe to call repeatedly. */
  async function prepare(): Promise<void> {
    if (index.value) {
      return;
    }
    if (!preparing) {
      isPreparing.value = true;
      preparing = build().finally(() => {
        isPreparing.value = false;
        preparing = null;
      });
    }
    await preparing;
  }

  /** The best matches for `query`, most relevant first. Empty until `prepare()` has finished. */
  function search(query: string): HelpSearchResult[] {
    if (!index.value || !query.trim()) {
      return [];
    }
    return index.value
      .search(query)
      .slice(0, MAX_RESULTS)
      .map(result => ({ path: result.path, title: result.title, guide: result.guide }));
  }

  watch(locale, () => {
    index.value = null;
  });

  return {
    prepare,
    search,
    isPreparing,
  };
}
